import { Component, ChangeDetectionStrategy, inject, computed } from '@angular/core';
import { RouterLink, Router, NavigationEnd } from '@angular/router';
import { NgOptimizedImage } from '@angular/common';
import { ToggleDarkMode } from '../../ui/toggle-dark-mode/toggle-dark-mode';
import { ProfileMenu } from '../../ui/profile-menu/profile-menu';
import { StatusBadge } from '../../ui/status-badge/status-badge';
import { AdminButtons } from './admin-buttons/admin-buttons';
import { OrganizationService } from '../../service/api/organization.service';
import { AuthService } from '../../../core/service/auth.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { shouldUseOwnCommunicationHighlights } from '../../utils/communication-context.utils';

@Component({
  selector: 'app-header',
  imports: [RouterLink, ToggleDarkMode, ProfileMenu, AdminButtons, NgOptimizedImage, StatusBadge],
  templateUrl: './header.html',
  styleUrl: './header.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Header {
  private organizationService = inject(OrganizationService);
  private authService = inject(AuthService);
  private router = inject(Router);

  readonly status = this.organizationService.status;
  readonly isAdmin = this.authService.isAdmin;
  readonly newCommunicationsNr = computed(
    () => this.authService.currentUser()?.new_comunications_nr || 0,
  );
  readonly shouldClearCommunicationsBadge = computed(() =>
    shouldUseOwnCommunicationHighlights(this.isAdmin(), this.isStepPage()),
  );

  onCommunicationsClick(): void {
    if (!this.shouldClearCommunicationsBadge()) {
      return;
    }

    this.authService.clearNewCommunicationsBadge();
  }

  // Status is managed through the OrganizationService signal, no need to call getStatus here

  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => (e as NavigationEnd).urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  readonly isSidebarHidden = computed(() => {
    const url = this.currentUrl();
    const isCommunications = url.startsWith('/communications');
    const isOrgContext = url.includes('orgContext=true');
    const isContractSignature = url.includes('/contract-signature');

    return (
      (this.isAdmin() &&
        (url === '/home' ||
          url === '/' ||
          (isCommunications && !isOrgContext) ||
          url === '/conservatori')) ||
      isContractSignature
    );
  });

  public readonly isStepPage = computed(() => {
    const url = this.currentUrl();
    return (
      url.startsWith('/base-info') ||
      url.startsWith('/document-typology') ||
      url.startsWith('/conservatori-user') ||
      url.startsWith('/tipologie-documenti-report') ||
      url.startsWith('/documenti-servizio-rdc') ||
      (url.startsWith('/communications') && url.includes('orgContext=true'))
    );
  });

  readonly isStatusVisible = computed(() => {
    const hasStatus = Boolean(this.status()?.last_status);
    if (!hasStatus) return false;
    if (this.isStepPage()) return true;
    const url = this.currentUrl();
    return !this.isAdmin() && (url === '/home' || url === '/');
  });
}
